import { PaginationResponse, AmlScanEntity, TableSetting, TableSettingRequest, LatestAmlReport, IngoingTransaction } from '@/app/api/api.model';
import { ApiService } from '@/app/api/api.service';
import { CopyButtonComponent } from '@/app/partials/components/copy-button/copy-button.component';
import { EmptyPaginationComponent } from '@/app/partials/components/empty-pagination/empty-pagination.component';
import { EmptyTableComponent } from '@/app/partials/components/empty-table/empty-table.component';
import { ReportStatusComponent } from '@/app/partials/components/report-status/report-status.component';
import { ReportsComponent } from '@/app/partials/components/reports/reports.component';
import { parseTransactionData } from '@/app/partials/helpers/transaction-parser.helper';
import { PopoverContainerComponent } from '@/app/partials/module/popover/popover-container/popover-container.component';
import { PopoverDirective } from '@/app/partials/module/popover/popover.directive';
import { CropWithDotsCenterPipe } from '@/app/partials/pipes/crop-with-dots-center.pipe';
import { CutNumberPipe } from '@/app/partials/pipes/cut-number.pipe';
import { TransformUnderscoreToSpacePipe } from '@/app/partials/pipes/tranform-underscore-to-space.pipe';
import { TriggerIdConvertToCategoryPipe } from '@/app/partials/pipes/trigger-id-convert-to-category.pipe';
import { AuthService } from '@/app/partials/services/auth.service';
import { SidenavService } from '@/app/partials/services/sidenav.service';
import { CdkDropList, CdkDrag, CdkDragHandle, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { DatePipe, NgClass, TitleCasePipe, LowerCasePipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed, effect, inject, input, InputSignal, OnInit, output, OutputEmitterRef, signal, WritableSignal } from '@angular/core';
import { ProjectSettingsService } from '@/app/partials/services/project-settings.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, NonNullableFormBuilder, FormGroup } from '@angular/forms';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatRipple } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatTable, MatColumnDef, MatHeaderCell, MatCell, MatHeaderRow, MatRow, MatNoDataRow, MatTableModule, MatTableDataSource } from '@angular/material/table';
import { Subscription, take, filter } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { changeNameTrxToTronPipe } from '@/app/partials/pipes/change-name-trx-to-tron.pipe';

// Настройки таблицы которые сохраняются в API
export interface EisSignalsTableSettings {
  allDisplayedColumns: string[];
  filteredDisplayedColumns: string[];
}

// Имя таблицы необходимо для сохранения настроек в API
export const tableNameEisSignals = 'EisSignalsTable';
@Component({
  selector: 'app-eis-signals-table-body',
  imports: [
    MatIconModule,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatHeaderRow,
    MatRow,
    MatFormFieldModule,
    MatRipple,
    MatPaginatorModule,
    MatNoDataRow,
    DatePipe,
    MatMenuTrigger,
    MatMenu,
    CdkDropList,
    CdkDrag,
    CdkScrollable,
    CdkDragHandle,
    MatCheckbox,
    FormsModule,
    MatTableModule,
    NgClass,
    TransformUnderscoreToSpacePipe,
    TitleCasePipe,
    MatSelectModule,
    ReactiveFormsModule,
    LowerCasePipe,
    CropWithDotsCenterPipe,
    CopyButtonComponent,
    PopoverDirective,
    PopoverContainerComponent,
    ReportStatusComponent,
    TriggerIdConvertToCategoryPipe,
    NgTemplateOutlet,
    EmptyTableComponent,
    EmptyPaginationComponent,
    CutNumberPipe,
    changeNameTrxToTronPipe,
  ],
  templateUrl: './eis-signals-table-body.component.html',
  styleUrl: './eis-signals-table-body.component.scss'
})
export class EisSignalsTableBodyComponent implements OnInit {
  private readonly projectSettingsService = inject(ProjectSettingsService);
  isAmlEnergyActive = computed(() => this.projectSettingsService.settings()?.isAmlEnergyActive ?? false);

  private readonly _apiService: ApiService = inject(ApiService);

  private _mainSidenavService: SidenavService = inject(SidenavService);

  private readonly _authService: AuthService = inject(AuthService);
  private readonly _route: ActivatedRoute = inject(ActivatedRoute);
  private readonly _router: Router = inject(Router);

  readonly pageEvent: OutputEmitterRef<PageEvent> = output<PageEvent>();

  readonly outputFilterForm: OutputEmitterRef<{
    amlScanType?: string,
    sourceType?: string,
    nickname?: string
  }> = output<{ amlScanType?: string, sourceType?: string, nickname?: string }>({});

  readonly paginationResponseData: InputSignal<PaginationResponse<AmlScanEntity>> = input.required<PaginationResponse<AmlScanEntity>>();

  readonly paginationOptions: InputSignal<{
    pageSize: number;
    pageIndex: number;
    pageSizeOptions: number[];
    length: number
  }> = input.required<{ pageSize: number; pageIndex: number; pageSizeOptions: number[]; length: number }>();

  readonly isGrouped: InputSignal<boolean> = input.required<boolean>();

  public markOpenRow: WritableSignal<string> = signal('');
  public nicknamesList: WritableSignal<string[]> = signal([]);
  public sortingList: WritableSignal<{ amlScanTypes: string[], sourceTypes: string[] }> = signal({
    amlScanTypes: [],
    sourceTypes: []
  });

  private readonly _fb = inject(NonNullableFormBuilder);

  public filterForm: FormGroup = this._fb.group({
    nickname: this._fb.control<string | null>(null),
    amlScanType: this._fb.control<string | null>(null),
    sourceType: this._fb.control<string | null>(null),
  });

  readonly tableSettings: InputSignal<TableSetting<EisSignalsTableSettings> | undefined> = input<TableSetting<EisSignalsTableSettings> | undefined>();

  allDisplayedColumns: string[] = [
    'date',
    'source',
    'risk_score',
    'deposit_wallet',
    'case_status',
    'value',
    'network',
    // 'risky_amount',
    'report_type',
    'action',
    'table_settings',
      // 'nickname',
    // 'coin',
    // 'monitoring_status',
    // 'category',
    // 'exposure',
    // 'service_fee',
    // 'blockchain_fee',


  ];

  private _viewSubscription!: Subscription;

  filteredDisplayedColumns: string[] = [];

  get displayedColumns() {
    return this.allDisplayedColumns.filter((column) => !this.filteredDisplayedColumns.includes(column));
  };

  get displayedColumnsEmpty() {
    const displayedColumns = this.displayedColumns;
    return displayedColumns.filter(item => item !== 'table_settings');
  };

  readonly dataSource: MatTableDataSource<AmlScanEntity> = new MatTableDataSource<AmlScanEntity>([]);

  constructor() {
    this.filterForm.valueChanges.pipe(takeUntilDestroyed()).subscribe({
      next: (value) => {
        this.outputFilterForm.emit(value);
      }
    });
    effect(() => {
      const { content } = this.paginationResponseData();
      this.dataSource.data = content;
    });

    effect(() => {
      const loadedTableSettings = this.tableSettings();

      if (loadedTableSettings) {
        const { data: { allDisplayedColumns, filteredDisplayedColumns } } = loadedTableSettings;
        // Проверка нужно для того чтоб если были изменения в allDisplayedColumns.
        // Мы не перезаписывали сохраненными настройками.
        if (
          this.allDisplayedColumns.every((displayedColumn) => allDisplayedColumns.includes(displayedColumn))
          &&
          allDisplayedColumns.every((displayedColumn) => this.allDisplayedColumns.includes(displayedColumn))
        ) {
          this.allDisplayedColumns = allDisplayedColumns;
          this.filteredDisplayedColumns = filteredDisplayedColumns;
        }
      }
    });
  }

  ngOnInit(): void {
    this._apiService.getWalletNicknames().pipe(take(1)).subscribe({
      next: (value: string[]) => {
        this.nicknamesList.set(value);
      }
    });
    this._apiService.getSortingOptionsMonitoring().pipe(take(1)).subscribe({
      next: (value: { amlScanTypes: string[], sourceTypes: string[] }) => {
        this.sortingList.set(value);
      }
    });
    const amlScanUuid = this._route.snapshot.queryParamMap.get('amlScanUuid');
    console.log(amlScanUuid);
    if (amlScanUuid) {
      this.viewReport(amlScanUuid);
      this._router.navigate([], {
        relativeTo: this._route,
        queryParams: { amlScanUuid: null, process: null },
        queryParamsHandling: 'merge'
      });
    }
  }

  private _saveTableSettings(): void {
    const user = this._authService.accountDto();

    if (!user) return;

    const { uuid } = user;

    const body: TableSettingRequest = {
      data: {
        allDisplayedColumns: this.allDisplayedColumns,
        filteredDisplayedColumns: this.filteredDisplayedColumns,
      },
      tableName: tableNameEisSignals,
      accountId: uuid
    };
    this._apiService.createOrUpdateTableSetting<EisSignalsTableSettings>(body).pipe(take(1)).subscribe({
      next: () => {
        console.log(`${ tableNameEisSignals } settings saved!`);
      }
    });
  }

  handlerChangeDisplayColumnCheckbox({ checked }: MatCheckboxChange, displayedColumn: string): void {
    if (checked) {
      this.filteredDisplayedColumns = this.filteredDisplayedColumns.filter((c) => c !== displayedColumn);
    } else {
      this.filteredDisplayedColumns.push(displayedColumn);
    }
    this._saveTableSettings();
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.allDisplayedColumns, event.previousIndex, event.currentIndex);
    this._saveTableSettings();
  }

  viewReport(uuid: string): void {
    this._viewSubscription?.unsubscribe();
    this._viewSubscription = this._apiService.getAmlScanByUuid(uuid).pipe(
      filter(Boolean)
    ).subscribe({
      next: (transactionEntity: AmlScanEntity) => {
        const transaction = parseTransactionData(transactionEntity);
        this.markOpenRow.set(transaction.uuid);
        let inputs = {
          inputTransaction: transaction
        };
        this._mainSidenavService.open<boolean>({
          template: ReportsComponent,
          inputs
        }).afterClosed.pipe(take(1)).subscribe({
          next: () => {
            this.markOpenRow.set('');
            this.pageEvent.emit(this.paginationOptions());
          }
        });
      }
    });
  }

  public asIngoingTransaction(transaction: AmlScanEntity<LatestAmlReport>): AmlScanEntity<IngoingTransaction> {
    return transaction as AmlScanEntity<IngoingTransaction>;
  }
}
